/**
 * One's own account.
 *
 * Separate from `settingsRepository`, which is a manager administering *other*
 * people and needs `users.manage` to do it. Everything here is available to
 * every signed-in user about themselves, cashiers included, so folding it into
 * the settings repository would tie a screen every user can open to a permission
 * most of them do not hold.
 */

import { api } from "@/api/client";

export interface Profile {
  id: string;
  staff_code: string;
  display_name: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string;
}

/** The fields a person may change about themselves. */
export type ProfilePatch = Pick<Profile, "first_name" | "last_name" | "email" | "phone">;

export interface PasswordChange {
  current_password: string;
  new_password: string;
  /** Blacklists every other refresh token. Defaults to true server-side. */
  revoke_other_sessions?: boolean;
}

/**
 * A fresh token pair.
 *
 * Changing a password revokes outstanding refresh tokens — including the
 * caller's own, since the server cannot tell one browser from another. It
 * therefore hands back a new pair, and the store swaps them in, so the person
 * who just changed their password is not signed out by their own success.
 */
export interface ReissuedTokens {
  access: string;
  refresh: string;
}

export const profileRepository = {
  get: () => api.get<Profile>("/auth/profile/"),
  update: (patch: Partial<ProfilePatch>) => api.patch<Profile>("/auth/profile/", patch),
  changePassword: (input: PasswordChange) =>
    api.post<ReissuedTokens>("/auth/password/", input),
};
